import * as React from 'react'

import { i18n } from '@thg-commerce/enterprise-core'
import { ImageProps } from '@thg-commerce/gravity-elements'
import { Carousel } from '@thg-commerce/gravity-patterns'
import { AmpCarouselProps } from '@thg-commerce/gravity-patterns/Carousel/types'
import { Grid } from '@thg-commerce/gravity-system'

import {
  SwatchSelectorContent,
  SwatchSelectorLink,
  SwatchSelectorCTA,
  SwatchSelectorTitle,
  ImageContainer,
  StyledContainer,
  StyledImage,
  Subtitle,
  Title,
  SwatchSelectorGridItem,
  LogoImageContainer,
  StyledLogo,
  StyledContent,
  SwatchSelectorThumbnail,
  CountThumbnail,
  CarouselControlsContainer,
} from './styles'

export interface SwatchSelectorSliderItemProps {
  logoImage: ImageProps
  content?: string
  swatches: {
    title: string
    thumbnail: ImageProps
    image: ImageProps & { link?: string }
  }[]
  count?: string
  CTA: {
    text?: string
    link?: string
  }
  extraCTA: {
    text?: string
    link?: string
  }
}

export interface SwatchSelectorSliderSetProps {
  items: SwatchSelectorSliderItemProps[]
  title?: string
  subtitle?: string
  className?: string
}

export const SwatchSelectorSliderItem = (
  props: SwatchSelectorSliderItemProps,
) => {
  const carouselText = {
    leftScrollLabel: i18n('carousel.controls.left.arialabel'),
    rightScrollLabel: i18n('carousel.controls.right.arialabel'),
    playPauseLabel: i18n('carousel.controls.playpause.arialabel'),
  }

  const [activeThumbnail, setActiveThumbnail] = React.useState(0)

  const ampProps: AmpCarouselProps = {
    height: 500,
    width: 300,
    type: 'slides',
    role: 'region',
  }

  const images = props.swatches.map((swatch, index) =>
    swatch.image.link ? (
      <a href={swatch.image.link} role="button">
        <StyledImage
          {...swatch.image}
          alt={swatch.image.alt || `swatch-image-${index}`}
        />
      </a>
    ) : (
      <StyledImage
        {...swatch.image}
        alt={swatch.image.alt || `swatch-image-${index}`}
      />
    ),
  )

  const thumbnails = props.swatches.map(
    (item, index) =>
      item.thumbnail && (
        <SwatchSelectorThumbnail
          {...item.thumbnail}
          height={44}
          width={44}
          key={index}
          tabIndex={0}
          testId={`thumbnail-item-${index}`}
          wrapperTestId={`thumbnail-container-item-${index}`}
          onClick={() => setActiveThumbnail(index)}
          onKeyDown={(e) => e.key === 'Enter' && setActiveThumbnail(index)}
          active={index === activeThumbnail}
        />
      ),
  )

  const customCarouselControls = props.count
    ? [
        ...thumbnails,
        <CountThumbnail
          key={props.swatches.filter((item) => item.thumbnail).length}
          data-testid="count-thumbnail"
          href={props.swatches[props.swatches.length - 1].image.link}
        >
          {props.count}
        </CountThumbnail>,
      ]
    : thumbnails

  return (
    <React.Fragment>
      <LogoImageContainer>
        <StyledLogo {...props.logoImage} />
      </LogoImageContainer>
      <StyledContent>
        <ImageContainer data-testid="swatch-selector-item-image-container">
          <Carousel
            items={images}
            isAmp={false}
            ampProps={ampProps}
            i18n={carouselText}
            indicatorStyle="hidden"
            itemsPerSlide={1}
            autoPlay={false}
            slideTo={activeThumbnail}
            hideSlidePreview
            hideControls
          />
        </ImageContainer>
        <CarouselControlsContainer>
          {customCarouselControls}
        </CarouselControlsContainer>
        <StyledContainer data-testid="swatch-selector-styled-container">
          {props.swatches.find((swatch) => swatch.title) &&
            props.swatches.map(
              (activeSwatch, index) =>
                index === activeThumbnail && (
                  <SwatchSelectorTitle
                    key={index}
                    data-testid={`swatch-selector-item-title-${index}`}
                  >
                    {activeSwatch.title}
                  </SwatchSelectorTitle>
                ),
            )}
          {props.content && <SwatchSelectorContent content={props.content} />}
          {props.CTA && (
            <SwatchSelectorCTA renderedAs="a" href={props.CTA.link}>
              {props.CTA.text}
            </SwatchSelectorCTA>
          )}
          {props.extraCTA && (
            <SwatchSelectorLink href={props.extraCTA.link}>
              {props.extraCTA.text}
            </SwatchSelectorLink>
          )}
        </StyledContainer>
      </StyledContent>
    </React.Fragment>
  )
}

export const SwatchSelectorSliderSet = (
  props: SwatchSelectorSliderSetProps,
) => {
  const swatchSelectorSliderItems = props.items.map(
    (item, index) =>
      item && (
        <SwatchSelectorGridItem key={index} colSpan={[12, 6, 6, 6]}>
          <SwatchSelectorSliderItem {...item} />
        </SwatchSelectorGridItem>
      ),
  )

  return (
    <div className={props.className}>
      {props.title && <Title>{props.title}</Title>}
      {props.subtitle && <Subtitle>{props.subtitle}</Subtitle>}
      <Grid>{swatchSelectorSliderItems}</Grid>
    </div>
  )
}
